class ShippingProgressBar extends HTMLElement {
  connectedCallback() {
    this.progressType = this.dataset.progressType || 'order_value';
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.current = parseInt(this.dataset.current, 10) || 0;
    this.itemCount = parseInt(this.dataset.itemCount, 10) || 0;
    this.weightQualification = this.dataset.weightQualification || 'at_or_below';
    this.alwaysUnlocked = this.dataset.alwaysUnlocked === 'true';
    this.baseCurrent = this.current;
    this.baseItemCount = this.itemCount;

    this.messageEl = this.querySelector('.shipping-progress-bar__message');
    this.trackEl = this.querySelector('.shipping-progress-bar__track');
    this.fillEl = this.querySelector('.shipping-progress-bar__fill');
    this.amountEl = this.querySelector('.shipping-progress-bar__amount');

    this.cartSection = this.closest('cart-page, cart-drawer');
    this.productSection = this.closest('section.product-hero, section.product-main') || this.closest('section');

    this._onCartInput = this._onCartInput.bind(this);
    this._onCartClick = this._onCartClick.bind(this);
    this._onProductInput = this._onProductInput.bind(this);
    this._onProductVariantChange = this._onProductVariantChange.bind(this);

    if (this.cartSection) {
      this.cartSection.addEventListener('input', this._onCartInput);
      this.cartSection.addEventListener('change', this._onCartInput);
      this.cartSection.addEventListener('click', this._onCartClick);
    }

    if (this.productSection && this.productSection.querySelector('variant-picker')) {
      this._initProductProjection();
    }

    this._render();
  }

  disconnectedCallback() {
    if (this.cartSection) {
      this.cartSection.removeEventListener('input', this._onCartInput);
      this.cartSection.removeEventListener('change', this._onCartInput);
      this.cartSection.removeEventListener('click', this._onCartClick);
    }
    if (this._picker) {
      this._picker.removeEventListener('change', this._onProductVariantChange);
    }
    if (this._quantityInput) {
      this._quantityInput.removeEventListener('input', this._onProductInput);
      this._quantityInput.removeEventListener('change', this._onProductInput);
    }
  }

  _initProductProjection() {
    this._picker = this.productSection.querySelector('variant-picker');
    this._quantityInput = this.productSection.querySelector('input[name="quantity"]');
    if (!this._picker) return;

    this._productJson = this._parseProductJson(this._picker);
    if (!this._productJson) return;

    this._picker.addEventListener('change', this._onProductVariantChange);
    if (this._quantityInput) {
      this._quantityInput.addEventListener('input', this._onProductInput);
      this._quantityInput.addEventListener('change', this._onProductInput);
    }

    this._updateProductProjection();
  }

  _parseProductJson(picker) {
    const script = picker.querySelector('script[data-product-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  _getSelectedOptions() {
    if (!this._picker) return [];
    const fieldsets = Array.from(this._picker.querySelectorAll('fieldset[data-option-position]'))
      .sort((a, b) => (a.dataset.optionPosition || 0) - (b.dataset.optionPosition || 0));
    return fieldsets.map((fieldset) => {
      const input = fieldset.querySelector('input:checked');
      return input ? input.value : '';
    });
  }

  _onProductVariantChange() {
    this._updateProductProjection();
  }

  _onProductInput() {
    this._updateProductProjection();
  }

  _updateProductProjection() {
    if (!this._productJson) return;
    const selectedOptions = this._getSelectedOptions();
    const variant = this._productJson.find((v) =>
      v.options && v.options.every((value, index) => value === selectedOptions[index])
    );
    if (!variant) return;

    const quantity = this._quantityInput ? Math.max(parseInt(this._quantityInput.value, 10) || 1, 1) : 1;
    this._currentVariant = variant;

    const add = this.progressType === 'product_weight'
      ? (variant.grams || 0) * quantity
      : (variant.price_cents || 0) * quantity;

    this.current = this.baseCurrent + add;
    this.itemCount = this.baseItemCount + quantity;
    this._render();
  }

  _onCartInput(event) {
    if (!this.cartSection) return;
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    this._computeCartFromRows();
  }

  _onCartClick(event) {
    if (!this.cartSection) return;
    const changeButton = event.target.closest('[data-cart-quantity-change]');
    const removeButton = event.target.closest('[data-cart-remove]');
    const clearButton = event.target.closest('[data-cart-clear]');

    if (clearButton) {
      this.current = 0;
      this.itemCount = 0;
      this._render();
      return;
    }

    if (removeButton) {
      this._computeCartFromRows(removeButton.dataset.key);
      return;
    }

    if (changeButton) {
      requestAnimationFrame(() => this._computeCartFromRows());
    }
  }

  _computeCartFromRows(excludedKey) {
    let total = 0;
    let weight = 0;
    let itemCount = 0;

    this.cartSection.querySelectorAll('[data-cart-item]').forEach((row) => {
      if (excludedKey && row.dataset.key === excludedKey) return;
      const input = row.querySelector('[data-cart-quantity-input]');
      if (!input) return;
      const quantity = parseInt(input.value, 10) || 0;
      if (quantity <= 0) return;

      const unitPrice = parseInt(row.dataset.itemUnitPrice, 10) || 0;
      const unitGrams = parseFloat(row.dataset.itemUnitGrams) || 0;

      total += unitPrice * quantity;
      weight += unitGrams * quantity;
      itemCount += quantity;
    });

    this.current = this.progressType === 'product_weight' ? weight : total;
    this.itemCount = itemCount;
    this._render();
  }

  _render() {
    if (this.alwaysUnlocked) {
      const message = this.progressType === 'product_weight'
        ? this._fill(this.dataset.messageUnlockedWeight, { current: this._formatWeight(this.current), threshold: this._formatWeight(this.threshold) })
        : this.dataset.messageReached || '';

      if (this.amountEl) this.amountEl.hidden = true;
      if (this.messageEl) this.messageEl.textContent = message;
      if (this.trackEl) {
        this.trackEl.setAttribute('aria-valuenow', this.trackEl.getAttribute('aria-valuemax') || this.threshold);
        this.trackEl.setAttribute('aria-label', message);
      }
      if (this.fillEl) this.fillEl.style.width = '100%';
      return;
    }

    if (this.progressType === 'product_weight') {
      this._renderWeight();
    } else {
      this._renderValue();
    }
  }

  _renderWeight() {
    const threshold = this.threshold;
    const current = Math.max(0, this.current);
    const remaining = Math.max(0, threshold - current);
    const excess = Math.max(0, current - threshold);
    const progress = threshold > 0 ? Math.min(100, (current / threshold) * 100) : 0;

    let message = '';
    if (this.weightQualification === 'at_or_below') {
      if (this.itemCount === 0) {
        message = this._fill(this.dataset.messageLimitWeight, { threshold: this._formatWeight(threshold) });
      } else if (current <= threshold) {
        message = this._fill(this.dataset.messageWithinLimitWeight, { current: this._formatWeight(current), threshold: this._formatWeight(threshold) });
      } else {
        message = this._fill(this.dataset.messageOverLimitWeight, { current: this._formatWeight(current), excess: this._formatWeight(excess), threshold: this._formatWeight(threshold) });
      }
    } else {
      if (remaining === 0 && this.itemCount > 0) {
        message = this.dataset.messageUnlockedWeight || '';
      } else {
        message = this._fill(this.dataset.messageProgressWeight, { current: this._formatWeight(current), remaining: this._formatWeight(remaining), threshold: this._formatWeight(threshold) });
      }
    }

    if (this.amountEl) this.amountEl.textContent = `${this._formatWeight(current)} / ${this._formatWeight(threshold)}`;
    if (this.messageEl) this.messageEl.textContent = message;
    if (this.trackEl) {
      this.trackEl.setAttribute('aria-valuemax', threshold);
      this.trackEl.setAttribute('aria-valuenow', Math.min(threshold, current));
      this.trackEl.setAttribute('aria-label', message);
    }
    if (this.fillEl) this.fillEl.style.width = `${progress}%`;
  }

  _renderValue() {
    const threshold = this.threshold;
    const current = Math.max(0, this.current);
    const remaining = Math.max(0, threshold - current);
    const progress = threshold > 0 ? Math.min(100, (current / threshold) * 100) : 0;

    let message = '';
    if (remaining === 0 && this.itemCount > 0) {
      message = this.dataset.messageReached || '';
    } else if (this.itemCount > 0) {
      message = this._fill(this.dataset.messageProgress, { amount: this._formatMoney(remaining) });
    } else {
      message = this._fill(this.dataset.messageDefault, { amount: this._formatMoney(threshold) });
    }

    if (this.messageEl) this.messageEl.textContent = message;
    if (this.trackEl) {
      this.trackEl.setAttribute('aria-valuemax', threshold);
      this.trackEl.setAttribute('aria-valuenow', Math.min(threshold, current));
      this.trackEl.setAttribute('aria-label', message);
    }
    if (this.fillEl) this.fillEl.style.width = `${progress}%`;
  }

  _fill(template, values) {
    if (!template) return '';
    let result = template;
    Object.entries(values).forEach(([key, value]) => {
      if (typeof value === 'undefined') return;
      result = result.split(`[${key}]`).join(value);
    });
    return result;
  }

  _formatMoney(cents) {
    if (typeof window.formatMoney === 'function') {
      try {
        return window.formatMoney(cents);
      } catch (e) {}
    }
    try {
      const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'USD';
      const lang = document.documentElement.lang || 'en';
      return new Intl.NumberFormat(lang, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  _formatWeight(grams) {
    return `${Math.round(grams)}g`;
  }
}

if (!customElements.get('shipping-progress-bar')) {
  customElements.define('shipping-progress-bar', ShippingProgressBar);
}
